import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, hotTags } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51cg吃瓜网 | 51cgfun今日吃瓜`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.summary,
    'image': article.image,
    'author': {
      '@type': 'Person',
      'name': article.author,
    },
    'datePublished': article.date,
    'publisher': {
      '@type': 'Organization',
      'name': '51cg吃瓜网',
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://51cg.fun/article/${article.id}`,
    },
    'keywords': article.tags.join(', '),
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 text-xs rounded-full bg-primary-100 text-primary-600 font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              {article.isTrending && (
                <span className="px-3 py-1 text-xs rounded-full bg-background-200 text-foreground-600 font-medium whitespace-nowrap">
                  趋势
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 51cg吃瓜网`}
              className="w-full h-auto object-cover max-h-[500px]"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/60">
            <span className="text-sm text-foreground-500 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-links-line text-accent-600"></i>
              </div>
              <h2 className="text-lg font-heading text-foreground-900">51cg相关吃瓜推荐</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}

        <section className="max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="bg-background-100 rounded-lg p-6 border border-background-200/60">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-hashtag text-accent-600"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900">51cg今日热点标签</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {hotTags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs rounded-full bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
    </>
  );
}